import { CarBasicInfoSectionDto } from '../types/models.types';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

/**
 * @param isoDate - Data w formacie ISO lub undefined
 * @returns Data w formacie DD.MM.YYYY lub null jeśli brak danych
 */
export const formatDateToDDMMYYYY = (isoDate?: string): string | null => {
  if (!isoDate) {
    return null;
  }

  return format(new Date(isoDate), 'dd.MM.yyyy', { locale: pl });
};

export interface MappedCarBasicInfo {
  vehicleBrand: string;
  vehicleModel: string;
  vinNumber: string;
  orderNumber?: string;
  packageType?: string;
  reportDate: string | null;
  pagesCount?: number;
  vehicleType: 'sedan' | 'van' | 'hatchback';
  logoUrl?: string;
  carImageUrl?: string;
  yearOfProduction?: string;
  mileage?: string;
  countryOfOrigin?: string;
  fuelType?: string;
  engineDisplacement?: string;
  enginePower?: string;
  driveType?: string;
  engineLayout?: string;
  transmission?: string;
  bodyType?: string;
  doorCount?: string;
  color?: string;
  maxWeight?: string;
  registrationNumber: string | null;
  insuranceExpiryDate: string | null;
  firstRegistrationDate: string | null;
  nextTechnicalInspectionDate: string | null;
  firstRegistrationInPoland: string | null;
  isRegistrationNumberConsistent: string | null;
}

const mapEngineLayoutToUI = (
  layout?: 'R' | 'V' | 'VR' | 'W' | 'BOXER' | 'WANKEL' | 'ELECTRIC',
  cylinderCount?: number,
): string => {
  if (!layout) return '';

  const layoutMap: Record<
    'R' | 'V' | 'VR' | 'W' | 'BOXER' | 'WANKEL' | 'ELECTRIC',
    string
  > = {
    R: `Rzędowy${cylinderCount ? ` / ${cylinderCount} cylindry` : ''}`,
    V: `V${cylinderCount ? cylinderCount : ''}`,
    VR: `VR${cylinderCount ? cylinderCount : ''}`,
    W: `W${cylinderCount ? cylinderCount : ''}`,
    BOXER: `Boxer${cylinderCount ? ` / ${cylinderCount} cylindry` : ''}`,
    WANKEL: 'Wankel',
    ELECTRIC: 'Elektryczny',
  };

  return layoutMap[layout] || '';
};

const mapTransmissionToUI = (
  transmission?: 'MANUAL' | 'SEMI_AUTOMATIC' | 'AUTOMATIC',
): string => {
  if (!transmission) return '';

  const transmissionMap: Record<
    'MANUAL' | 'SEMI_AUTOMATIC' | 'AUTOMATIC',
    string
  > = {
    MANUAL: 'Manualna',
    SEMI_AUTOMATIC: 'Półautomatyczna',
    AUTOMATIC: 'Automatyczna',
  };

  return transmissionMap[transmission] || '';
};

const mapFuelTypeToUI = (
  fuelType?:
    | 'GASOLINE'
    | 'GASOLINE_CNG'
    | 'GASOLINE_LPG'
    | 'DIESEL'
    | 'ELECTRIC'
    | 'ETHANOL'
    | 'HYBRID'
    | 'HYDROGEN',
): string => {
  if (!fuelType) return '';

  const fuelMap: Record<
    | 'GASOLINE'
    | 'GASOLINE_CNG'
    | 'GASOLINE_LPG'
    | 'DIESEL'
    | 'ELECTRIC'
    | 'ETHANOL'
    | 'HYBRID'
    | 'HYDROGEN',
    string
  > = {
    GASOLINE: 'Benzyna',
    GASOLINE_CNG: 'Benzyna + CNG',
    GASOLINE_LPG: 'Benzyna + LPG',
    DIESEL: 'Diesel',
    ELECTRIC: 'Elektryczny',
    ETHANOL: 'Etanol',
    HYBRID: 'Hybrydowy',
    HYDROGEN: 'Wodór',
  };

  return fuelMap[fuelType] || '';
};

const mapDriveTypeToUI = (
  driveType?: 'FRONT_WHEEL' | 'REAR_WHEEL' | 'AWD_4X4',
): string => {
  if (!driveType) return '';

  const driveMap: Record<'FRONT_WHEEL' | 'REAR_WHEEL' | 'AWD_4X4', string> = {
    FRONT_WHEEL: 'Napęd na przód',
    REAR_WHEEL: 'Napęd na tył',
    AWD_4X4: '4x4 (AWD)',
  };

  return driveMap[driveType] || '';
};

type BodyType =
  | 'HATCHBACK'
  | 'SEDAN'
  | 'SUV'
  | 'LIFTBACK'
  | 'STATION_WAGON'
  | 'BUS'
  | 'VAN'
  | 'HATCHBACK_2_3_DOORS'
  | 'SEDAN_4_5_DOORS';

const mapBodyTypeToUI = (bodyType?: BodyType): string => {
  if (!bodyType) return '';

  const bodyMap: Record<BodyType, string> = {
    HATCHBACK: 'Hatchback',
    HATCHBACK_2_3_DOORS: 'Hatchback',
    SEDAN: 'Sedan',
    SEDAN_4_5_DOORS: 'Sedan',
    LIFTBACK: 'Liftback',
    STATION_WAGON: 'Kombi',
    SUV: 'SUV',
    BUS: 'Bus',
    VAN: 'Van',
  };

  return bodyMap[bodyType] || '';
};

type Doors = 'DOORS_2_3' | 'DOORS_4_5';

const mapDoorsToUI = (doors?: Doors): string => {
  if (!doors) return '';

  const doorsMap: Record<Doors, string> = {
    DOORS_2_3: '2/3',
    DOORS_4_5: '4/5',
  };

  return doorsMap[doors] || '';
};

/**
 * Mapuje bodyType + doors z API na vehicleType używany w komponentach wizualizacji.
 * Dla BUS/VAN zawsze zwraca 'van'.
 * Dla pozostałych typów nadwozia wizualizacja zależy od liczby drzwi:
 *   DOORS_2_3 → 'hatchback', DOORS_4_5 → 'sedan'.
 */
const mapBodyTypeToVehicleType = (
  bodyType?: BodyType,
  doors?: Doors,
): 'sedan' | 'van' | 'hatchback' => {
  if (!bodyType) return 'sedan';

  if (bodyType === 'BUS' || bodyType === 'VAN') return 'van';

  if (doors === 'DOORS_2_3') return 'hatchback';
  if (doors === 'DOORS_4_5') return 'sedan';

  // Fallback na starą logikę gdy brak pola doors
  const vehicleTypeMap: Record<BodyType, 'sedan' | 'van' | 'hatchback'> = {
    HATCHBACK: 'hatchback',
    HATCHBACK_2_3_DOORS: 'hatchback',
    SEDAN: 'sedan',
    SEDAN_4_5_DOORS: 'sedan',
    LIFTBACK: 'hatchback',
    STATION_WAGON: 'sedan',
    SUV: 'sedan',
    BUS: 'van',
    VAN: 'van',
  };

  return vehicleTypeMap[bodyType] || 'sedan';
};

const formatMileage = (mileage?: number, unit?: 'KM' | 'MILES'): string => {
  if (!mileage) return '';

  const formattedNumber = new Intl.NumberFormat('pl-PL').format(mileage);
  const unitText = unit === 'MILES' ? 'mil' : 'km';

  return `${formattedNumber} ${unitText}`;
};

const formatPower = (powerHP?: number, powerKW?: number): string => {
  if (!powerHP && !powerKW) return '';

  const hpText = powerHP ? `${powerHP} KM` : '';
  const kwText = powerKW ? `${powerKW} kW` : '';

  if (hpText && kwText) {
    return `${hpText} / ${kwText}`;
  }

  return hpText || kwText;
};

const formatDisplacement = (displacement?: number): string => {
  if (!displacement) return '';

  return `${displacement} cm³`;
};

const formatWeight = (weight?: number): string => {
  if (!weight) return '';

  return `${weight} kg`;
};

const mapDocumentConsistency = (
  consistency?: 'YES' | 'NO' | 'NONE' | 'OTHER',
): string | null => {
  if (!consistency) return null;

  const consistencyMap: Record<'YES' | 'NO' | 'NONE' | 'OTHER', string> = {
    YES: 'TAK',
    NO: 'NIE',
    NONE: 'Brak',
    OTHER: 'Inne',
  };

  return consistencyMap[consistency] || null;
};

export const mapCarBasicInfoSectionToUI = (
  section: CarBasicInfoSectionDto | null | undefined,
): MappedCarBasicInfo => {
  if (!section || !section.fields) {
    return {
      vehicleBrand: '',
      vehicleModel: '',
      vinNumber: '',
      reportDate: null,
      vehicleType: 'sedan',
      yearOfProduction: '',
      mileage: '',
      countryOfOrigin: '',
      fuelType: '',
      engineDisplacement: '',
      enginePower: '',
      driveType: '',
      engineLayout: '',
      transmission: '',
      bodyType: '',
      doorCount: '',
      color: '',
      maxWeight: '',
      registrationNumber: null,
      insuranceExpiryDate: null,
      firstRegistrationDate: null,
      nextTechnicalInspectionDate: null,
      firstRegistrationInPoland: null,
      isRegistrationNumberConsistent: null,
    };
  }

  const fields = section.fields;

  // Extract registration number
  const registrationNumber =
    fields.registrationNumberInfo?.type === 'REGISTRATION_NUMBER_AVAILABLE'
      ? (fields.registrationNumberInfo.registrationNumber ?? null)
      : fields.registrationNumberInfo?.type === 'NO_REGISTRATION_NUMBER_INFORMATION'
        ? 'Brak informacji'
        : null;

  // Extract insurance expiry date
  let insuranceExpiryDate: string | null = null;
  if (fields.insuranceInfo?.type === 'INSURED') {
    insuranceExpiryDate = formatDateToDDMMYYYY(fields.insuranceInfo.expiryDate);
  } else if (fields.insuranceInfo?.type === 'UNINSURED') {
    insuranceExpiryDate = 'Nieubezpieczony';
  } else if (fields.insuranceInfo?.type === 'NO_INSURANCE_INFORMATION') {
    insuranceExpiryDate = 'Brak informacji';
  }

  // Extract first registration date
  let firstRegistrationDate: string | null = null;
  if (fields.firstRegistrationInfo?.type === 'FIRST_REGISTERED') {
    firstRegistrationDate = formatDateToDDMMYYYY(fields.firstRegistrationInfo.registrationDate);
  } else if (fields.firstRegistrationInfo?.type === 'NO_FIRST_REGISTRATION_INFORMATION') {
    firstRegistrationDate = 'Brak informacji';
  }

  // Extract next technical inspection date
  let nextTechnicalInspectionDate: string | null = null;
  if (fields.inspectionInfo?.type === 'INSPECTION_SCHEDULED') {
    nextTechnicalInspectionDate = formatDateToDDMMYYYY(fields.inspectionInfo.nextInspectionDate);
  } else if (fields.inspectionInfo?.type === 'NO_CURRENT_INSPECTION') {
    nextTechnicalInspectionDate = 'Brak aktualnego przeglądu';
  } else if (fields.inspectionInfo?.type === 'NO_INSPECTION_INFORMATION') {
    nextTechnicalInspectionDate = 'Brak informacji';
  }

  // Extract first registration in Poland
  let firstRegistrationInPoland: string | null = null;
  if (fields.polandRegistrationInfo?.type === 'REGISTERED_IN_POLAND') {
    firstRegistrationInPoland = formatDateToDDMMYYYY(fields.polandRegistrationInfo.registrationDate);
  } else if (fields.polandRegistrationInfo?.type === 'UNREGISTERED_IN_POLAND') {
    firstRegistrationInPoland = 'Niezarejestrowany';
  } else if (fields.polandRegistrationInfo?.type === 'NO_POLAND_REGISTRATION_INFORMATION') {
    firstRegistrationInPoland = 'Brak informacji';
  }

  return {
    vehicleBrand: fields.brand || '',
    vehicleModel: fields.model || '',
    vinNumber: fields.vin || '',
    reportDate: null,
    vehicleType: mapBodyTypeToVehicleType(fields.bodyType, fields.doors),
    yearOfProduction: fields.yearOfProduction || '',
    mileage: formatMileage(fields.mileage, fields.mileageUnit),
    countryOfOrigin: fields.countryOfOrigin || '',
    fuelType: mapFuelTypeToUI(fields.fuelType),
    engineDisplacement: formatDisplacement(fields.displacement),
    enginePower: formatPower(fields.powerHP, fields.powerKW),
    driveType: mapDriveTypeToUI(fields.driveType),
    engineLayout: mapEngineLayoutToUI(fields.engineLayout, fields.cylinderCount),
    transmission: mapTransmissionToUI(fields.transmissionType),
    bodyType: mapBodyTypeToUI(fields.bodyType),
    doorCount: mapDoorsToUI(fields.doors),
    color: fields.color || '',
    maxWeight: formatWeight(
      fields.vehicleWeightInfo?.grossVehicleWeight as number | undefined,
    ),
    registrationNumber,
    insuranceExpiryDate,
    firstRegistrationDate,
    nextTechnicalInspectionDate,
    firstRegistrationInPoland,
    isRegistrationNumberConsistent: mapDocumentConsistency(fields.documentConsistency),
  };
};


